import { Paper } from "@mui/material";
import PlanHeaderWithoutPlanCode from "../../../../../shared/enrollment/planHeaderWithoutPlanCode/PlanHeaderWithoutPlanCode";
import CircleIcon from "@mui/icons-material/Circle";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { useContext, useState, useMemo, useCallback, useEffect, Suspense } from "react";
import {
	AuthContext,
	GroupEnrollmentContext,
	GroupOwnerContext,
	PaycheckContext,
	ThemeContext
} from "../../../../../../contexts";
import FormSingleAction from "../../../../../shared/enrollment/formSingleAction/FormSingleAction";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import { useHistory, useLocation } from "react-router-dom";

import "./gltdForm.css";
import { PlanFormProps } from "../../../../../../types/components/enrollmentProps.types";
import PlanActions from "../../../../../shared/enrollment/planActions/PlanActions";
import { CustomFormDialogType } from "../../../../../../types/components/dialogProps.types";
import {
	generateGLTDClass1WaiveEnrollmentPayload,
	generateGSTDWaiveEnrollmentPayload
} from "../../../../../../utils/commonFunctions/groupEnrollment/enrollment";
import { AddEnrollmentPayload } from "../../../../../../utils/api/enrollment/enrollment";
import { api } from "../../../../../../utils/api";
import { getCurrentDate, manualDateComparator } from "../../../../../../utils/commonFunctions/date";
import { CommonDetails } from "../../../../../../types/enrollment.types";
import { trackPromise } from "react-promise-tracker";
import { LazyCustomFormDialog } from "../../../../../shared";

const GltdForm = ({ getEnrollmentInitData, openEnrollment }: PlanFormProps): JSX.Element => {
	const { theme } = useContext(ThemeContext);
	const { member } = useContext(AuthContext);
	const { setCurrentEnrollment, deleteCurrentEnrollment } = useContext(GroupEnrollmentContext);
	const { paycheck } = useContext(PaycheckContext);
	const history = useHistory();
	const location = useLocation();
	const [waiveReason, setWaiveReason] = useState("");
	const [isWaiveEnrollmentTriggered, setIsWaiveEnrollmentTriggered] = useState(false);
	const urlSearchParams: URLSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const { groupOwner } = useContext(GroupOwnerContext);

	const handleWaiveReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setWaiveReason(value);
	};

	const [customFormDialogProps, setCustomFormDialogProps] = useState<CustomFormDialogType>();

	const handleWaiveEnrollment = useCallback(async () => {
		if (member === null || openEnrollment === null) {
			return;
		}
		const { standardDetails, commonDetails } = generateGLTDClass1WaiveEnrollmentPayload(
			member,
			openEnrollment,
			waiveReason
		);
		const enrollmentPayload: AddEnrollmentPayload = {
			standard_details: standardDetails,
			common_details: { ...commonDetails, agent_id: groupOwner && groupOwner._id ? groupOwner._id : null }
		};
		const response = await trackPromise(api.enrollment.addEnrollment(enrollmentPayload));
		if (response) {
			getEnrollmentInitData(false);
		}
		const step = urlSearchParams.get("step");
		history.push(`?step=${step}&stage=2`);
	}, [getEnrollmentInitData, history, member, openEnrollment, urlSearchParams, waiveReason]);

	const handleWaiveButtonClick = useCallback(() => {
		setCustomFormDialogProps(
			Object.assign({}, customFormDialogProps, {
				openDialog: true,
				title: "Are you sure want to waive this enrollment?",
				textfields: [
					{
						label: "Waive Reason",
						onChange: handleWaiveReasonChange,
						value: waiveReason,
						placeholder: "Enter your reason for waive"
					}
				],
				actions: [
					{
						label: "Cancel",
						callback: () =>
							setCustomFormDialogProps(Object.assign({}, customFormDialogProps, { openDialog: false }))
					},
					{
						label: "Yes",
						callback: () => {
							setCustomFormDialogProps(
								Object.assign({}, customFormDialogProps, {
									openDialog: false
								})
							);
							// handleWaiveEnrollment();
							setIsWaiveEnrollmentTriggered(true);
						}
					}
				]
			})
		);
	}, [customFormDialogProps, handleWaiveEnrollment, waiveReason]);

	const handleActivateEnrollment = useCallback(async () => {
		if (member === null || openEnrollment === null || paycheck === null) {
			return;
		}

		const currentDate = new Date() as unknown as string;
		const planStartDate = openEnrollment.plan.start_date; // -1 or 0
		const planDateComparison: number = manualDateComparator(planStartDate, currentDate);

		const effectiveDate = openEnrollment.plan.auto_approve
			? planDateComparison === -1 || planDateComparison === 0
				? getCurrentDate()
				: null
			: null;

		const enrollmentCommonDetails: CommonDetails = {
			agent_id: groupOwner && groupOwner._id ? groupOwner._id : null,
			location_number: member.location.location_number,
			location_name: member.location.location_name,
			group_number: member.group_number,
			group_name: member.group.name,
			plan_object_id: openEnrollment.plan._id,
			plan_code: openEnrollment.plan.plan_code,
			plan_name: openEnrollment.plan.plan_name,
			base_plan_name: openEnrollment.plan.plan_name,
			insured_object_id: member._id,
			insured_SSN: member.SSN,
			writing_number: 0,
			unenrolled_reason: null,
			waive_reason: null,
			termination_reason: null,
			enrollment_date: getCurrentDate(),
			effective_date: effectiveDate,
			termination_date: null,
			open_enrollment_id: openEnrollment._id,
			total_premium: 0
		};
		const enrollmentPayload: AddEnrollmentPayload = {
			common_details: enrollmentCommonDetails,
			standard_details: [
				{
					member_object_id: member._id,
					member_SSN: member.SSN,
					coverage_code: "EE",
					premium_amount: 0,
					is_tobacco: false
				}
			]
		};
		const response = await api.enrollment.addEnrollment(enrollmentPayload);
		if (response) {
			const step = urlSearchParams.get("step");
			getEnrollmentInitData(false);
			history.push(`?step=${step}&stage=0`);
		}
	}, [getEnrollmentInitData, history, member, openEnrollment, paycheck, urlSearchParams]);

	useEffect(() => {
		if (openEnrollment) {
			// setCurrentEnrollment(
			// 	Object.assign(
			// 		{},
			// 		{
			// 			plan_name: openEnrollment.plan.plan_name,
			// 			status: "Current",
			// 			premium_amount: 0
			// 		}
			// 	)
			// );
		}
	}, [openEnrollment, setCurrentEnrollment]);

	useEffect(() => {
		if (isWaiveEnrollmentTriggered) {
			handleWaiveEnrollment();
			setIsWaiveEnrollmentTriggered(false); // Reset the trigger
		}
	}, [isWaiveEnrollmentTriggered, handleWaiveEnrollment]);

	return (
		<div className="GLTD-classes theme-container">
			<Suspense fallback={<div />}>
				{customFormDialogProps ? <LazyCustomFormDialog customFormDialogProps={customFormDialogProps} /> : null}
			</Suspense>
			<Paper className="paper-container">
				<PlanHeaderWithoutPlanCode planName="Group Long Term Disability (Class 1)" />
				<div className="theme-plan-section-margin" />
				<div className="theme-plan-icon-item-center-container">
					<div>
						<ClearAllIcon className="theme-plan-point-header-icon" style={{ color: theme.primary_color }} />
					</div>
					<div className="theme-plan-point-header-text">Class One</div>
				</div>
				<div className="theme-plan-sub-inner-section-margin" />
				<div style={{ marginLeft: "20px" }}>
					<div className="theme-plan-description-text">
						Long Term Disability (LTD) Plan offers you security of earnings (upon approval from Lincoln
						Financial Group) should you become disabled.
					</div>
					<div className="theme-plan-sub-inner-section-margin" />
					<div className="theme-plan-icon-item-container">
						<div>
							<CreateOutlinedIcon className="theme-plan-point-icon" />
						</div>
						<div className="theme-plan-point-text">
							The LTD benefit is a company-paid benefit for all designated full-time employees; there is
							no cost to the insured.
						</div>
					</div>
					<br></br>
					<div className="theme-plan-icon-item-container">
						<div>
							<CreateOutlinedIcon className="theme-plan-point-icon" />
						</div>
						<div className="theme-plan-point-text">Elimination period: 180 days.</div>
					</div>
					<br></br>
					<div className="theme-plan-icon-item-container">
						<div>
							<CreateOutlinedIcon className="theme-plan-point-icon" />
						</div>
						<div className="theme-plan-point-text">
							The benefit pays the insured 50% of base pay to age 65, or until no longer disabled.
						</div>
					</div>
					<div className="sub-point-section">
						<div className="theme-plan-icon-item-center-container">
							<div>
								<CircleIcon className="theme-plan-subpoint-icon" />
							</div>
							<div className="theme-plan-subpoint-text">Maximum Disability Benefit $1,500/mo</div>
						</div>
						<br></br>
						<div className="theme-plan-icon-item-center-container">
							<div>
								<CircleIcon className="theme-plan-subpoint-icon" />
							</div>
							<div className="theme-plan-subpoint-text">Minimum Disability Benefit $100/mo</div>
						</div>
						<br></br>
						<div className="theme-plan-icon-item-center-container">
							<div>
								<CircleIcon className="theme-plan-subpoint-icon" />
							</div>
							<div className="theme-plan-subpoint-text">
								Claims are processed for approval and paid by Lincoln Financial Group (if approved)
							</div>
						</div>
					</div>
				</div>
				<div className="theme-plan-section-margin" />
				<PlanActions
					waiveButtonCallback={handleWaiveButtonClick}
					activateButtonCallback={handleActivateEnrollment}
				/>
			</Paper>
		</div>
	);
};

export default GltdForm;
